import { z } from "zod";
import { idSchema, singleLineTextSchema } from "./text.js";
// Impostazioni della scheda del Lead (piano M2, Task 9; D6, D13, D17): «Campi della scheda», cioè il modello
// versionato della scheda e i campi dell'azienda, e «Chi vede i Lead», cioè il perimetro dei ruoli di livello 1.
// Leggerle è di ogni appartenenza (in sola lettura senza la funzione); cambiarle richiede `settings.lead_fields` o
// `settings.lead_visibility`. La validazione vera del modello è quella del server (`validateCardModel`): qui c'è
// soltanto la forma, con i limiti tecnici sulla dimensione del corpo. Il nome di un campo e quello di una voce seguono la
// regola dei nomi di `text.ts` (decisione del 1° ottobre 2026: un a capo diventa uno spazio, i caratteri invisibili o
// che girano il verso si rifiutano); la chiave di un campo che cambia arriva a una query e ha la forma di un
// identificativo, così un NUL risponde `422` e non `500`.
const labelSchema = singleLineTextSchema(80);
const FIELD_TYPES = ["number", "currency_eur", "choice", "text"];
const fieldTypeSchema = z.enum(FIELD_TYPES);
// «Contatto e responsabilità» e «Salone e qualificazione», più «Territorio» nei modelli pubblicati prima del 1° ottobre
// 2026 (decisione del product owner: il luogo sta nel contatto).
export const cardModelSchema = z.object({
    sections: z
        .array(z.object({
        key: z.enum(["contact", "salon", "territory"]),
        visible: z.boolean(),
        fields: z.array(z.object({ ref: z.string().max(80), visible: z.boolean() })).max(60),
    }))
        .min(2)
        .max(3),
}); // stessa forma di `CardModel` del Task 3; la validazione vera è `validateCardModel`
/**
 * Un campo dell'azienda, anche archiviato. `usedBy` conta i Lead attivi (né archiviati né uniti a un altro) con un valore
 * nel campo: tutti per chi cambia i campi, soltanto quelli che vede per chi legge e basta. La regola «il tipo cambia
 * soltanto finché nessun Lead ha un valore» conta invece ogni valore, anche dei Lead archiviati o uniti, quindi con
 * `usedBy` a 0 una pubblicazione può ancora essere rifiutata: `card_model_invalid` con `details.valuesOnAnyLead`, i
 * valori su tutti i Lead, archiviati e uniti compresi. Sono due conteggi con due nomi: `usedBy` non è mai quello del
 * rifiuto.
 */
export const fieldDefinitionSchema = z.object({
    id: z.string(),
    key: z.string(),
    label: z.string(),
    type: fieldTypeSchema,
    options: z.array(z.object({ id: z.string(), label: z.string(), hidden: z.boolean() })),
    archived: z.boolean(),
    usedBy: z.number().int(), // quanti Lead hanno un valore
});
export const leadFieldsResponseSchema = z.object({
    version: z.number().int(),
    model: cardModelSchema,
    definitions: z.array(fieldDefinitionSchema),
    canEdit: z.boolean(),
});
/**
 * Corpo di `POST /api/settings/lead-fields/publish`: la versione di partenza, i campi nuovi, le modifiche ai campi
 * esistenti e il modello intero della versione nuova. `update.options`, quando c'è, è l'elenco intero delle voci
 * nell'ordine voluto: una voce che manca si toglie (soltanto se nessun Lead l'ha scelta), una voce con un id nuovo
 * si aggiunge. Il modello richiama soltanto campi non archiviati: un campo archiviato esce dalla scheda.
 */
export const publishLeadFieldsBodySchema = z.strictObject({
    baseVersion: z.number().int().min(1),
    create: z
        .array(z.strictObject({
        key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
        label: labelSchema,
        type: fieldTypeSchema,
        options: z.array(z.strictObject({ id: z.string().regex(/^[a-z0-9_]{1,40}$/), label: labelSchema })).max(50),
    }))
        .max(20),
    update: z
        .array(z.strictObject({
        key: idSchema,
        label: labelSchema.optional(),
        type: fieldTypeSchema.optional(),
        options: z.array(z.strictObject({ id: z.string(), label: labelSchema, hidden: z.boolean() })).max(50).optional(),
        archived: z.boolean().optional(),
    }))
        .max(50),
    model: cardModelSchema,
});
export const publishLeadFieldsResponseSchema = z.object({ version: z.number().int() });
/** «Chi vede i Lead»: «Tutti i Lead» (`all`) oppure «Soltanto i suoi» (`own`), per i ruoli di livello 1. */
export const leadVisibilitySchema = z.strictObject({ mode: z.enum(["all", "own"]) });
/** `mixed` quando i ruoli di livello 1 non sono d'accordo, per esempio dopo una modifica in «Ruoli e permessi». */
export const leadVisibilityResponseSchema = z.object({ mode: z.enum(["all", "own", "mixed"]), canEdit: z.boolean() });
//# sourceMappingURL=lead-fields.js.map